import moment from "moment";

import { TransferablePatientInfoResponse } from "@/Common/Api/ExternalCommunications/Dto/TransferablePatientInfoResponse";
import { PhoneNumber } from "@/Common/Models/Contacts/PhoneNumber";
import { Person } from "@/Common/Models/Person/Person";

import img_p_banner_icon_100 from "@/Globals/_images/p-banner-icon-100.png";

class ExternalPatientImportAdvancedSearchRenderer {
    private readonly HIGHTLIGHT_CLASS: string = "highlight-yellow";

    private patient: Person;
    private isImportable: boolean;
    private className: string;
    private index: number;
    private onPatientSelect: (patient: Person) => void;

    private patientInfoForHighlighting: TransferablePatientInfoResponse;

    public static render(patient: Person, patientInfoForHilight: TransferablePatientInfoResponse, isImportable: boolean, className?: string, index?: number, onSelect?: (patient: Person) => void): JSX.Element {
        let renderer = new ExternalPatientImportAdvancedSearchRenderer(patient, patientInfoForHilight, isImportable, className, index, onSelect);
        return renderer.render();
    }

    private constructor(patient: Person, patientInfoForHighlight: TransferablePatientInfoResponse, isImportable: boolean, className: string, index: number, onSelect: (patient: Person) => void) {
        this.patient = patient;
        this.isImportable = isImportable;
        this.className = className;
        this.index = index;
        this.onPatientSelect = onSelect;
        this.patientInfoForHighlighting = patientInfoForHighlight;
    }

    private render(): JSX.Element {
        return (
            <div className={this.className} onClick={() => this.onPatientSelect(this.patient)} key={this.patient.ContactId + `_${this.index}`}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <div className="flex-container">
                        {this.renderPatientNameSection()}
                    </div>
                </div>

                <div style={{ width: "5px" }} />

                <div style={{ display: "flex", flexDirection: "column" }}>
                    {!this.isImportable && (
                        <div style={{ fontSize: "12px", fontStyle: "italic" }}>
                            {cacheStore.GetString("PatientAlreadyLinkedLabel")}
                        </div>
                    )}
                    <div className="flex-container">
                        {this.renderPatientInfoSection()}
                        {this.renderPatientContactSection()}
                    </div>
                </div>
            </div>
        );
    }

    private renderPatientNameSection(): JSX.Element {
        return (
            <div className="flex-col r1">
                <img title={this.patient.NameWithIdentifier} src={img_p_banner_icon_100} style={{ width: "48px" }} alt={this.patient.NameWithIdentifier} />
            </div>
        );
    }

    private renderPatientInfoSection(): JSX.Element {
        return (
            <div className="flex-col r2">
                <h4 className="light-blue">
                    <span className={this.isSameLastName ? this.HIGHTLIGHT_CLASS : ""}> {this.patient.LastName}</span>
                    <span>, </span>
                    <span className={this.isSameFirstName ? this.HIGHTLIGHT_CLASS : ""}>{this.patient.FirstName}</span>
                    <span className="myle-icon-16 female" />
                </h4>
                <ul>
                    <li>
                        <strong>{sessionStore.getDefaultHealthCardLabel()} </strong>
                        <span className={this.isSameNAM ? this.HIGHTLIGHT_CLASS : ""}>{this.patient.GetFormattedNAM()} {this.patient.Patient.GetFormattedNAMExpirationDateString()}</span>
                    </li>
                    <li>
                        <strong>{cacheStore.GetString("DateofBirthLabel")} </strong>
                        <span className={this.isSameDOB ? this.HIGHTLIGHT_CLASS : ""}>{`${this.patient.Patient.GetFormattedDateOfBirth()} ${this.patient.Patient.GetFormattedAge()}`}</span>
                    </li>
                </ul>
            </div>
        );
    }

    private renderPatientContactSection(): JSX.Element {
        return (
            <div className="flex-col r3">
                <ul>
                    {this.renderPhones()}
                    {this.renderAddress()}
                </ul>
            </div>
        );
    }

    private renderPhones(): JSX.Element[] {
        if (isArrayNullOrUndefinedOrEmpty(this.patient.Patient.Phones))
            return null;

        return this.patient.Patient.Phones.map((phone, idx) => {
            let listClass = "content-text";
            if (this.doesPhoneExist(phone.Number))
                listClass += ` ${this.HIGHTLIGHT_CLASS}`;

            return (
                <li key={idx}><span className="myle-icon-16 phone" /><span className={listClass}>{new PhoneNumber(phone).format()}</span></li>
            );
        });
    }

    private renderAddress(): JSX.Element {
        let formattedAddress = this.patient.Patient.GetFormattedAddress();
        if (!formattedAddress)
            return null;

        let listClass = "content-text";
        if (this.doesAddressExist(formattedAddress))
            listClass += ` ${this.HIGHTLIGHT_CLASS}`;

        return (
            <li><span className="myle-icon-16 pin" /><span className={listClass}>{formattedAddress}</span></li>
        );
    }

    private doesPhoneExist(phone: string): boolean {
        if (!phone || isArrayNullOrUndefinedOrEmpty(this.patientInfoForHighlighting.Phones))
            return false;

        return this.patientInfoForHighlighting.Phones.any(x => x.PhoneNumber === phone);
    }

    private doesAddressExist(address: string): boolean {
        if (!address || isArrayNullOrUndefinedOrEmpty(this.patientInfoForHighlighting.Addresses))
            return false;

        return this.patientInfoForHighlighting.Addresses.any(x => `${x.StreetAddress || ""}, ${x.City || ""}` === address);
    }

    private get isSameDOB(): boolean {
        if (!this.patient.DateOfBirth || !this.patientInfoForHighlighting.DateOfBirth)
            return false;

        return moment(this.patient.DateOfBirth).isSame(moment(this.patientInfoForHighlighting.DateOfBirth), "day");
    }

    private get isSameNAM(): boolean {
        if (!this.patient.NAM || isArrayNullOrUndefinedOrEmpty(this.patientInfoForHighlighting.HealthIdentifiers))
            return false;

        return this.patient.NAM === this.patientInfoForHighlighting.HealthIdentifiers[0].Value;
    }

    private get isSameFirstName(): boolean {
        if (!this.patient.FirstName || !this.patientInfoForHighlighting.FirstName)
            return false;

        return this.patient.FirstName.toLowerCase() === this.patientInfoForHighlighting.FirstName.toLowerCase();
    }

    private get isSameLastName(): boolean {
        if (!this.patient.LastName || !this.patientInfoForHighlighting.LastName)
            return false;

        return this.patient.LastName.toLowerCase() === this.patientInfoForHighlighting.LastName.toLowerCase();
    }
}

export { ExternalPatientImportAdvancedSearchRenderer };
